import { trackHeaderEvent } from '@comeon/analytics';
import { TracedRoutes } from '@comeon/error-reporter/src/traced-routes';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, useLocation, useParams } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import styled from 'styled-components';
import { selectAllBalances, selectCurrency, selectLocale } from '~/components/account/ducks/index.js';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { SITE_CODES } from '~/components/brand/constants';
import {
	selectHasPromotions,
	selectIsNetherlandsRegulatedFranchise,
	selectIsShopEnabled
} from '~/components/brand/ducks/index.js';
import {
	selectCasinoSubMenuItems,
	selectDefaultProduct,
	selectGlobalNavigationEnabled,
	selectIsDesktop,
	selectShowNewCookieBanner,
	selectSiteCode,
	selectUseCustomLogos
} from '~/components/brand/ducks/selectors.js';
import { gamesSelector } from '~/components/casino-shared/all-games/ducks/selectors';
import { gamesBySubProviderCodeSelector } from '~/components/casino-shared/ducks/selectors';
import Icon, { ICON_ARROW_LEFT, ICON_MENU } from '~/components/icon/index.js';
import { Bigger, Smaller } from '~/components/layout/mediaquery';
import Anchor from '~/components/misc/anchor.js';
import { selectPromotionsIsAvailableForUser } from '~/components/promotions/ducks/selectors';
import {
	ROUTE_ABOUT,
	ROUTE_BETTING_TIPS,
	ROUTE_COMPLAINT_PROCEDURE,
	ROUTE_COOKIE_POLICY,
	ROUTE_FREEBET,
	ROUTE_GLOBAL,
	ROUTE_HIGHLIGHT_GAMES,
	ROUTE_INSPIRED,
	ROUTE_LEGAL_BETTING,
	ROUTE_LIVEBETTING,
	ROUTE_PRIVACYPOLICY,
	ROUTE_PROMOTIONS,
	ROUTE_RESPONSIBLEGAMING,
	ROUTE_SHOP,
	ROUTE_SPORTSBOOK,
	ROUTE_SPORTSBOOK_BROWSE,
	ROUTE_SPORTSBOOK_LIVE_PAGE,
	ROUTE_TAX,
	ROUTE_TERMSANDCONDITIONS,
	ROUTE_TVBET,
	ROUTE_WE_SPIN,
	SPORTSBOOK_ROUTES,
	ROUTE_SPORTSBOOK_SEARCH_PAGE,
	ROUTE_SPORTSBOOK_MY_BETS,
	ROUTE_FREE_TO_PLAY_CUSTOM,
	ROUTE_HUB
} from '~/components/router/constants.js';
import ThemeContext, { useTheme } from '~/components/theme/theme-context.js';
import useTranslations from '~/components/translation/use-translations.ts';
import dataAt from '~/components/utilities/data-at.js';
import {
	ROUTE_WE_SPIN_GAME_VIEW as NEW_ROUTE_WE_SPIN_GAME_VIEW,
	WeSpinDesktopGameViewHeader as NewFlowWeSpinDesktopGameViewHeader
} from '~/components/we-spin/new-flow/index.js';
import { selectConfiguration } from '~/components/we-spin/old-flow/ducks/we-spin-selectors';
import {
	ROUTE_WE_SPIN_GAME_VIEW as OLD_ROUTE_WE_SPIN_GAME_VIEW,
	WeSpinDesktopGameViewHeader as OldFlowWeSpinDesktopGameViewHeader
} from '~/components/we-spin/old-flow/index.js';
import { useCloseContextForLocation } from '~/hooks/use-close-context.js';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import useGetDimensions from '~/hooks/use-get-dimensions.js';
import useIsContextLocationOpen from '~/hooks/use-is-context-location-open.js';
import useRouterCallbacks from '~/hooks/use-router-callbacks';
import currencyFormatter from '~/libs/currency-formatter.js';
import { getBasePathName } from '~/utils/pathname';
import breakpoint from '~/utils/style-utils.ts';
import CustomHeaderLogoMobile from './custom-header-logo-mobile';
import getHeaderLink from './header-logo-link';
import HeaderLogoMobile from './header-logo-mobile.js';
import useCasinoRoutes from '../router/use-casino-routes';
import useConfiguration from '~/configuration/use-configuration';

const Nav = styled.div.attrs(dataAt)`
	${props => (props.isAuthenticated ? 'display: flex;' : 'display: grid;')};
	grid-template-columns: repeat(3, 1fr);
	align-items: center;
	width: 100%;
	min-height: var(--smallerHeaderHeight);
	padding: 0 var(--smallerFrame);
	.svg-logo {
		fill: var(--page-head-logo, rgba(var(--base-02), 1));
		height: var(--page-head-logo-height, 40px);
		justify-self: center;
		max-width: 176px;
	}
	span {
		flex: 1;
		color: var(--page-head-title, rgba(var(--base-02), 1));
		text-align: center;
	}
	${breakpoint.lg`
		justify-content: space-between;
		min-height: var(--headerHeight);
		padding: 0 var(--biggerFrame);
		.svg-logo {
		    justify-self: start;
		}
	`};
`;

const IconBack = styled.div.attrs(dataAt)`
	border: none;
	background-color: transparent;
	width: 24px;
	display: flex;
	flex: 1;
	svg {
		fill: var(--page-head-icon, rgba(var(--base-02), 1));
	}
	&:focus {
		outline: none;
	}
	a {
		padding-left: 12px;
	}
`;

export const ThemedLogo = ({ useCustomLogos }) => {
	const isDesktop = useSelector(selectIsDesktop);
	if (useCustomLogos) {
		return <CustomHeaderLogoMobile isDesktop={isDesktop} />;
	}

	return (
		<div>
			<ThemeContext.Consumer>{({ Logo }) => <Logo data-at="logo-test" />}</ThemeContext.Consumer>
		</div>
	);
};

const ThemedLogoShort = () => {
	return (
		<div>
			<ThemeContext.Consumer>{({ LogoShort }) => <LogoShort data-at="logo-favicon" />}</ThemeContext.Consumer>
		</div>
	);
};

const ThemedLogoMobile = () => {
	return (
		<div>
			<ThemeContext.Consumer>{({ LogoMobile }) => <LogoMobile data-at="logo-mobile" />}</ThemeContext.Consumer>
		</div>
	);
};

const Title = styled.span.attrs(dataAt)`
	align-items: center;
	display: flex;
	font: var(--h4-bold);
	justify-content: center;
	white-space: nowrap;
	text-transform: var(--brand-text-transform, none);
	${'flex: 1;'};
`;

export const PageHeaderTitle = ({ title }) => {
	return title ? <Title dataAt="heading-title-mobile">{title}</Title> : <div />;
};

const PageLeftNav = () => {
	const { pushBack } = useRouterCallbacks();

	const navigateBack = useCallback(
		e => {
			if (e) e.preventDefault();
			pushBack();
		},
		[pushBack]
	);
	return (
		<IconBack onClick={navigateBack} dataAt="back-general-global-overlay" className="filter-page-head-back">
			<Icon type={ICON_ARROW_LEFT} />
		</IconBack>
	);
};
const LeftSideIconWrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	${props => (props.isAuthenticated ? 'flex: 1;' : '')};
`;

const LogoSmallIconWrapper = styled.div`
	display: flex;
	width: 100%;
	button {
		height: 100%;
		${props => (props.useCustomLogos ? 'width: 34px;' : '')};
	}
`;

const getProviderName = (providerNameParam, providerGames) => {
	const providerName = providerGames?.find(game => game.provider.code === providerNameParam);
	return providerName?.provider?.name ?? '';
};

const ProviderPageHeaderTitle = () => {
	const providerGames = useSelector(gamesSelector);
	const { providerName } = useParams();
	const title = useMemo(() => getProviderName(providerName, providerGames), [providerName, providerGames]);
	return <PageHeaderTitle title={title} />;
};

const SubProviderPageHeaderTitle = () => {
	const { subproviderCode } = useParams();
	const gamesBySubProviderCode = useSelector(gamesBySubProviderCodeSelector);

	return (
		<PageHeaderTitle
			title={gamesBySubProviderCode[subproviderCode.toUpperCase()]?.[0]?.casinoSubProviderVO?.name || ''}
		/>
	);
};

const BalanceTitle = () => {
	const { totalBalance } = useSelector(selectAllBalances);
	const currency = useSelector(selectCurrency);
	const locale = useSelector(selectLocale);
	return <PageHeaderTitle title={currencyFormatter.format(totalBalance, currency, locale)} />;
};

const SubMenuPageHeaderTitle = ({ items }) => {
	const params = useParams();
	const submenu = items?.find(item => item.urlPath?.includes(params.submenu));

	return <PageHeaderTitle title={submenu?.name} />;
};

const TEXT_KEYS = [
	'MOBILE_ALL_GAMES_BUTTON',
	'MOBILE_EXPLORE_BUTTON',
	'MOBILE_LIVE_CASINO_BUTTON',
	'MOBILE_MY_GAMES_TITLE',
	'SIDE_MENU_PRIVACY_POLICY',
	'MOBILE_HELP_PAGE_SUBMENU_TERMS',
	'SIDE_MENU_RESPONSIBLE_GAMING',
	'MOBILE_HELP_PAGE_SUBMENU_COOKIE_POLICY',
	'MOBILE_ABOUT_US_MAINTITLE',
	'MOBILE_TVBET_HEADER',
	'HIGHLIGHT_GAMES_HEADER_TITLE',
	'MOBILE_MAIN_MENU_SPORTS',
	'MOBILE_MAIN_MENU_LIVEBETTING',
	'PZBUK_TAX_HEADER_TITLE',
	'PZBUK_BETTING_TIPS_HEADER_TITLE',
	'PZBUK_FREEBET_HEADER_TITLE',
	'PZBUK_LEGAL_BETTING_HEADER_TITLE',
	'PRODUCT_INSPIRED',
	'MARKETPLACE_FILTER_TABS_PROMOTIONS',
	'MOBILE_MAIN_MENU_SHOP',
	'SPORTSBOOK_LIVE',
	'SPORTSBOOK_BROWSE_NAV_LABEL',
	'HUB_PAGE_TITLE',
	'SPORTSBOOK_SEARCH_INPUT_PLACEHOLDER',
	'SPORTSBOOK_MY_BETS_OPEN_BETS_TAB_LABEL'
];

const RoutesPageTitles = memo(
	({
		showCasinoExplore = true,
		showSubmenu = true,
		showCasinoAllGames = true,
		showCasinoMyGame = true,
		showLiveCasino = true,
		showMarketPlace = true,
		isAuthenticated = false
	}) => {
		const isPzbuk = useSelector(selectSiteCode) === SITE_CODES.PZBUK;
		const subMenuItems = useSelector(selectCasinoSubMenuItems);
		const showNewCookieBanner = useSelector(selectShowNewCookieBanner);
		const promotionsAvailableForUser = useSelector(selectPromotionsIsAvailableForUser);
		const hasPromotions = useSelector(selectHasPromotions);
		const authenticated = useSelector(authenticationSelector);
		const isShopEnabled = useSelector(selectIsShopEnabled);
		const isNetherlandsRegulatedFranchise = useSelector(selectIsNetherlandsRegulatedFranchise);
		const { translations: tr, isLoadingTranslations } = useTranslations(TEXT_KEYS);

		const {
			ROUTE_CASINO_EXPLORE,
			ROUTE_CASINO_ALL_GAMES,
			ROUTE_CASINO_SUBMENU,
			ROUTE_LIVE_CASINO_SUBMENU,
			ROUTE_CASINO_MY_GAMES,
			ROUTE_CASINO_MY_GAMES_LOVED,
			ROUTE_CASINO_SEARCH,
			ROUTE_LIVE_CASINO_EXPLORE,
			ROUTE_LIVE_CASINO_ALL_GAMES,
			ROUTE_LIVE_CASINO_MY_GAMES,
			ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
			ROUTE_LIVE_CASINO_SEARCH,
			ROUTE_CASINO_PROVIDER,
			ROUTE_CASINO_SUBPROVIDER
		} = useCasinoRoutes();

		if (isLoadingTranslations) {
			return <div className="spinner" />;
		}
		return (
			<TracedRoutes>
				{showCasinoExplore && (
					<Route path={ROUTE_CASINO_EXPLORE} element={<PageHeaderTitle title={tr.MOBILE_EXPLORE_BUTTON} />} />
				)}
				{showCasinoAllGames && (
					<Route
						path={ROUTE_CASINO_ALL_GAMES}
						element={<PageHeaderTitle title={tr.MOBILE_ALL_GAMES_BUTTON} />}
					/>
				)}
				{showSubmenu && (
					<Route
						path={`${ROUTE_CASINO_SUBMENU}/:submenu`}
						element={<SubMenuPageHeaderTitle items={subMenuItems?.casino} />}
					/>
				)}
				{showSubmenu && (
					<Route
						path={`${ROUTE_LIVE_CASINO_SUBMENU}/:submenu`}
						element={<SubMenuPageHeaderTitle items={subMenuItems?.live_casino} />}
					/>
				)}
				{showCasinoMyGame && (
					<>
						<Route
							path={ROUTE_CASINO_MY_GAMES}
							element={<PageHeaderTitle title={tr.MOBILE_MY_GAMES_TITLE} />}
						/>
						<Route
							path={ROUTE_CASINO_MY_GAMES_LOVED}
							element={<PageHeaderTitle title={tr.MOBILE_MY_GAMES_TITLE} />}
						/>
					</>
				)}
				<Route path={ROUTE_CASINO_SEARCH} element={<PageHeaderTitle title="" />} />
				{showLiveCasino && (
					<Route
						path={ROUTE_LIVE_CASINO_EXPLORE}
						element={<PageHeaderTitle title={tr.MOBILE_LIVE_CASINO_BUTTON} />}
					/>
				)}
				<Route
					path={ROUTE_LIVE_CASINO_ALL_GAMES}
					element={<PageHeaderTitle title={tr.MOBILE_ALL_GAMES_BUTTON} />}
				/>

				<Route
					path={ROUTE_LIVE_CASINO_MY_GAMES}
					element={<PageHeaderTitle title={tr.MOBILE_MY_GAMES_TITLE} />}
				/>
				<Route
					path={ROUTE_LIVE_CASINO_MY_GAMES_LOVED}
					element={<PageHeaderTitle title={tr.MOBILE_MY_GAMES_TITLE} />}
				/>

				<Route path={ROUTE_LIVE_CASINO_SEARCH} element={<PageHeaderTitle title="" />} />

				<Route path={ROUTE_PRIVACYPOLICY} element={<PageHeaderTitle title={tr.SIDE_MENU_PRIVACY_POLICY} />} />
				{isNetherlandsRegulatedFranchise && (
					<Route
						path={ROUTE_COMPLAINT_PROCEDURE}
						element={<PageHeaderTitle title={tr.SIDE_MENU_COMPLAINT_PROCEDURE} />}
					/>
				)}

				<Route
					path={ROUTE_TVBET}
					element={isAuthenticated ? <BalanceTitle /> : <PageHeaderTitle title={tr.MOBILE_TVBET_HEADER} />}
				/>

				<Route
					path={ROUTE_TERMSANDCONDITIONS}
					element={<PageHeaderTitle title={tr.MOBILE_HELP_PAGE_SUBMENU_TERMS} />}
				/>

				<Route
					path={ROUTE_RESPONSIBLEGAMING}
					element={<PageHeaderTitle title={tr.SIDE_MENU_RESPONSIBLE_GAMING} />}
				/>

				{showNewCookieBanner && (
					<Route
						path={ROUTE_COOKIE_POLICY}
						element={<PageHeaderTitle title={tr.MOBILE_HELP_PAGE_SUBMENU_COOKIE_POLICY} />}
					/>
				)}
				<Route path={ROUTE_ABOUT} element={<PageHeaderTitle title={tr.MOBILE_ABOUT_US_MAINTITLE} />} />

				{isAuthenticated && <Route path={SPORTSBOOK_ROUTES} element={<BalanceTitle />} />}
				{!isAuthenticated && (
					<>
						<Route
							path={`${ROUTE_SPORTSBOOK}/*`}
							element={<PageHeaderTitle title={tr.MOBILE_MAIN_MENU_SPORTS} />}
						/>
						<Route
							path={ROUTE_SPORTSBOOK_LIVE_PAGE}
							element={<PageHeaderTitle title={tr.SPORTSBOOK_LIVE} />}
						/>
						<Route
							path={ROUTE_SPORTSBOOK_BROWSE}
							element={<PageHeaderTitle title={tr.SPORTSBOOK_BROWSE_NAV_LABEL} />}
						/>
						<Route
							path={ROUTE_SPORTSBOOK_SEARCH_PAGE}
							element={<PageHeaderTitle title={tr.SPORTSBOOK_SEARCH_INPUT_PLACEHOLDER} />}
						/>
						<Route path={ROUTE_SPORTSBOOK_MY_BETS} element={<PageHeaderTitle title="" />} />
					</>
				)}
				{!isAuthenticated && (
					<Route
						path={ROUTE_LIVEBETTING}
						element={<PageHeaderTitle title={tr.MOBILE_MAIN_MENU_LIVEBETTING} />}
					/>
				)}
				<Route path={`${ROUTE_CASINO_PROVIDER}/:providerName`} element={<ProviderPageHeaderTitle />} />
				<Route path={`${ROUTE_CASINO_SUBPROVIDER}/:subproviderCode`} element={<SubProviderPageHeaderTitle />} />
				<Route
					path={ROUTE_INSPIRED}
					element={isAuthenticated ? <BalanceTitle /> : <PageHeaderTitle title={tr.PRODUCT_INSPIRED} />}
				/>
				<Route
					path={ROUTE_HIGHLIGHT_GAMES}
					element={
						isAuthenticated ? <BalanceTitle /> : <PageHeaderTitle title={tr.HIGHLIGHT_GAMES_HEADER_TITLE} />
					}
				/>
				<Route path={ROUTE_WE_SPIN} element={<PageHeaderTitle title="WeSpin" />} />
				<Route path={ROUTE_FREE_TO_PLAY_CUSTOM} element={<PageHeaderTitle title=" " />} />
				{isPzbuk && (
					<>
						<Route
							key="freebet"
							path={ROUTE_FREEBET}
							element={<PageHeaderTitle title={tr.PZBUK_FREEBET_HEADER_TITLE} />}
						/>
						<Route
							key="legal-betting"
							path={ROUTE_LEGAL_BETTING}
							element={<PageHeaderTitle title={tr.PZBUK_LEGAL_BETTING_HEADER_TITLE} />}
						/>
						<Route
							key="betting-tips"
							path={ROUTE_BETTING_TIPS}
							element={<PageHeaderTitle title={tr.PZBUK_BETTING_TIPS_HEADER_TITLE} />}
						/>
						<Route
							key="tax"
							path={ROUTE_TAX}
							element={<PageHeaderTitle title={tr.PZBUK_TAX_HEADER_TITLE} />}
						/>
					</>
				)}
				{showMarketPlace && (
					<>
						{!hasPromotions ||
						(hasPromotions && authenticated && isShopEnabled && !promotionsAvailableForUser) ? (
							<Route path={ROUTE_SHOP} element={<PageHeaderTitle title={tr.MOBILE_MAIN_MENU_SHOP} />} />
						) : (
							<Route path={ROUTE_SHOP} element={<PageHeaderTitle title="" />} />
						)}
						{!isShopEnabled ? (
							<Route
								path={ROUTE_PROMOTIONS}
								element={<PageHeaderTitle title={tr.MARKETPLACE_FILTER_TABS_PROMOTIONS} />}
							/>
						) : (
							<Route path={ROUTE_PROMOTIONS} element={<PageHeaderTitle title="" />} />
						)}
					</>
				)}
				<Route path={ROUTE_HUB} element={<PageHeaderTitle title={tr.HUB_PAGE_TITLE} />} />
				<Route path="/" element={<PageHeaderTitle title=" " />} />
			</TracedRoutes>
		);
	}
);

const Header = ({
	showCasinoExplore = true,
	showSubmenu = true,
	showCasinoAllGames = true,
	showCasinoMyGame = true,
	showLiveCasino = true,
	showMarketPlace = true,
	smallerShowLogo = true,
	logoLink = undefined,
	Burger,
	children
}) => {
	const router = useRouter();
	const contextualPathTransformer = useContextualPathTransformer();
	const isGlobalNavOpen = useIsContextLocationOpen(ROUTE_GLOBAL);
	const closeGlobalNav = useCloseContextForLocation(ROUTE_GLOBAL);
	const { LOGO_MOBILE_WIDTH } = useTheme();
	const { pathname } = useLocation();
	const [currentProduct, setCurrentProduct] = useState(null);
	const useCustomLogos = useSelector(selectUseCustomLogos);
	const { weSpinNewFlowEnabled } = useSelector(selectConfiguration);
	const { license } = useConfiguration();
	const globalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);

	const onBurgerClick = useCallback(
		e => {
			e.preventDefault();
			if (isGlobalNavOpen) {
				closeGlobalNav();
			} else {
				router.push(contextualPathTransformer(ROUTE_GLOBAL));
			}
		},
		[router, contextualPathTransformer, isGlobalNavOpen, closeGlobalNav]
	);

	const defaultProduct = useSelector(selectDefaultProduct);
	const isAuthenticated = useSelector(authenticationSelector);

	const theLogoLink = logoLink ?? currentProduct;

	const logoSmallWrapper = useRef();

	const logoDimensions = useGetDimensions(logoSmallWrapper);

	const renderPageTitles = !isAuthenticated;

	useEffect(() => {
		const headerLink = pathname.split('/').filter(el => el)[0];
		if (!currentProduct || currentProduct !== headerLink) {
			setCurrentProduct(getHeaderLink(headerLink, license) || getBasePathName(license, defaultProduct)());
		}
	}, [pathname, currentProduct, defaultProduct, license]);

	const { ROUTE_CASINO_PROVIDER, ROUTE_CASINO_SUBPROVIDER } = useCasinoRoutes();

	const PAGELEFT_NAV_ROUTES = useMemo(
		() => [
			`${ROUTE_CASINO_PROVIDER}/:providerName`,
			`${ROUTE_CASINO_SUBPROVIDER}/:subproviderCode`,
			ROUTE_TERMSANDCONDITIONS,
			ROUTE_PRIVACYPOLICY,
			ROUTE_RESPONSIBLEGAMING,
			ROUTE_COOKIE_POLICY,
			ROUTE_ABOUT
		],
		[ROUTE_CASINO_PROVIDER, ROUTE_CASINO_SUBPROVIDER]
	);

	return (
		<Nav dataAt="nav-layout-header" isAuthenticated={isAuthenticated}>
			<Bigger>
				<TracedRoutes>
					{weSpinNewFlowEnabled ? (
						<Route
							path={`${NEW_ROUTE_WE_SPIN_GAME_VIEW}/:ref`}
							element={<NewFlowWeSpinDesktopGameViewHeader />}
						/>
					) : (
						<Route
							path={`${OLD_ROUTE_WE_SPIN_GAME_VIEW}/:ref`}
							element={<OldFlowWeSpinDesktopGameViewHeader />}
						/>
					)}
					<Route
						path="*"
						element={
							<>
								<LeftSideIconWrapper>
									<Anchor
										onClick={() => {
											router.push(contextualPathTransformer(theLogoLink));
											trackHeaderEvent('Logo icon');
										}}
										data-at="brand-logo-header"
									>
										<ThemedLogo useCustomLogos={useCustomLogos} />
									</Anchor>
								</LeftSideIconWrapper>
								{!isAuthenticated && <div />}
							</>
						}
					/>
				</TracedRoutes>
			</Bigger>
			<Smaller>
				<TracedRoutes>
					{PAGELEFT_NAV_ROUTES.map(path => (
						<Route path={path} key={path} element={<PageLeftNav />} />
					))}
					<Route
						path="*"
						element={
							<LeftSideIconWrapper isAuthenticated={isAuthenticated}>
								{!globalNavigationEnabled && (
									<Burger onClick={onBurgerClick} dataAt="hamburger-menu">
										<Icon type={ICON_MENU} />
									</Burger>
								)}
								{smallerShowLogo && (
									<LogoSmallIconWrapper ref={logoSmallWrapper} useCustomLogos={useCustomLogos}>
										<Anchor
											onClick={() => {
												router.push(contextualPathTransformer(theLogoLink));
												trackHeaderEvent('Logo icon');
											}}
											data-at="brand-logo-header"
										>
											{!useCustomLogos && !isAuthenticated && LOGO_MOBILE_WIDTH && (
												<ThemedLogoShort />
											)}
											{!useCustomLogos && isAuthenticated && (
												<HeaderLogoMobile
													availableWidthForLogo={logoDimensions?.width ?? 0}
													LogoFull={ThemedLogoMobile}
													LogoFavicon={ThemedLogoShort}
												/>
											)}
											{useCustomLogos && <CustomHeaderLogoMobile />}
										</Anchor>
									</LogoSmallIconWrapper>
								)}
							</LeftSideIconWrapper>
						}
					/>
				</TracedRoutes>
				{renderPageTitles && (
					<RoutesPageTitles
						showCasinoExplore={showCasinoExplore}
						showSubmenu={showSubmenu}
						showCasinoAllGames={showCasinoAllGames}
						showCasinoMyGame={showCasinoMyGame}
						showLiveCasino={showLiveCasino}
						showMarketPlace={showMarketPlace}
						isAuthenticated={isAuthenticated}
					/>
				)}
			</Smaller>
			{children}
		</Nav>
	);
};

export default Header;
